/**
 * Parseur d'ordonnance — règle 14.
 *
 * Notation utilisée dans le cahier de l'opticien :
 *   `180'-0,25/-0,75`  -> AXE 180, SPH -0,25, CYL -0,75
 *   `-0,25/-0,75`      -> SPH et CYL, pas d'axe
 *   `-0,25`            -> SPH seule
 *   `PL`               -> plan (SPH = 'PL')
 *   `` (vide)          -> tout à null
 *
 * Les valeurs restent du TEXTE : le signe et les décimales sont conservés tels
 * quels, jamais convertis en float (un `-0,00` doit rester `-0,00`).
 * Tout ce que la grammaire refuse est conservé brut et déclenche `a_verifier`.
 */

export type OeilOrdonnance = {
  axe: string | null;
  sph: string | null;
  cyl: string | null;
};

export type ParseOeilResult = OeilOrdonnance & {
  /** false => la saisie n'est pas vide mais n'a pas pu être analysée. */
  ok: boolean;
  /** La saisie d'origine, intacte. */
  raw: string | null;
};

export const OEIL_VIDE: OeilOrdonnance = { axe: null, sph: null, cyl: null };

const NOMBRE = String.raw`[+\-−–]?\d+(?:[.,]\d+)?`;
const PLAN = String.raw`PL|PLAN|PLANO`;

const RE_OEIL = new RegExp(
  String.raw`^\s*(?:(\d{1,3})\s*['’´°º]\s*)?(${PLAN}|${NOMBRE})(?:\s*[/\\]\s*(${PLAN}|${NOMBRE}))?\s*$`,
  'i',
);

const RE_NOMBRE_SEUL = new RegExp(String.raw`^\s*(${NOMBRE})\s*$`);

/**
 * Normalise un nombre d'ordonnance : virgule décimale, signe conservé,
 * tirets typographiques ramenés au signe moins ASCII.
 */
function normaliserNombre(v: string): string {
  return v.trim().replace(/[−–]/g, '-').replace('.', ',');
}

function normaliserValeur(v: string): string {
  if (/^(PL|PLAN|PLANO)$/i.test(v.trim())) return 'PL';
  return normaliserNombre(v);
}

/** Analyse la saisie d'un œil (OD ou OG). */
export function parseOeil(raw: string | null | undefined): ParseOeilResult {
  const brut = typeof raw === 'string' ? raw.trim() : '';
  if (brut === '') return { ...OEIL_VIDE, ok: true, raw: null };

  const m = RE_OEIL.exec(brut);
  if (!m) return { ...OEIL_VIDE, ok: false, raw: brut };

  const [, axe, sph, cyl] = m;
  return {
    axe: axe ? String(Number(axe)) : null,
    sph: sph ? normaliserValeur(sph) : null,
    cyl: cyl ? normaliserValeur(cyl) : null,
    ok: true,
    raw: brut,
  };
}

/** Analyse la colonne ADD (addition) : un nombre seul, ou rien. */
export function parseAddition(raw: string | null | undefined): {
  addition: string | null;
  ok: boolean;
  raw: string | null;
} {
  const brut = typeof raw === 'string' ? raw.trim() : '';
  if (brut === '') return { addition: null, ok: true, raw: null };

  const m = RE_NOMBRE_SEUL.exec(brut);
  if (!m || !m[1]) return { addition: null, ok: false, raw: brut };

  return { addition: normaliserNombre(m[1]), ok: true, raw: brut };
}

/** Colonnes d'ordonnance telles qu'elles sont stockées sur une commande. */
export type OrdonnanceCommande = {
  od_raw: string | null;
  og_raw: string | null;
  od_axe: string | null;
  od_sph: string | null;
  od_cyl: string | null;
  og_axe: string | null;
  og_sph: string | null;
  og_cyl: string | null;
  addition: string | null;
  /** Vrai si au moins une saisie non vide n'a pas pu être analysée. */
  aVerifier: boolean;
};

/**
 * Analyse les trois saisies d'une commande d'un coup — règle 14.
 *
 * Partagée entre l'API et l'aperçu d'import : ce que le navigateur montre doit
 * être exactement ce que le serveur écrira. Rien n'est jamais perdu : le texte
 * d'origine reste dans `od_raw` / `og_raw`, et `aVerifier` signale ce qui
 * demande une correction manuelle.
 */
export function analyserOrdonnance(
  odRaw: string | null | undefined,
  ogRaw: string | null | undefined,
  additionRaw: string | null | undefined,
): OrdonnanceCommande {
  const od = parseOeil(odRaw);
  const og = parseOeil(ogRaw);
  const add = parseAddition(additionRaw);

  return {
    od_raw: od.raw,
    og_raw: og.raw,
    od_axe: od.axe,
    od_sph: od.sph,
    od_cyl: od.cyl,
    og_axe: og.axe,
    og_sph: og.sph,
    og_cyl: og.cyl,
    // Une addition illisible est conservée telle quelle plutôt que perdue.
    addition: add.addition ?? add.raw,
    aVerifier: !od.ok || !og.ok || !add.ok,
  };
}

/** Reconstruit la notation de l'opticien à partir des champs analysés. */
export function formatOeil(oeil: OeilOrdonnance): string {
  if (!oeil.sph && !oeil.cyl && !oeil.axe) return '';
  const axe = oeil.axe ? `${oeil.axe}'` : '';
  const sph = oeil.sph ?? '';
  const cyl = oeil.cyl ? `/${oeil.cyl}` : '';
  return `${axe}${sph}${cyl}`;
}

/** Vrai si l'œil ne porte aucune valeur. */
export function oeilEstVide(oeil: OeilOrdonnance): boolean {
  return !oeil.axe && !oeil.sph && !oeil.cyl;
}
